import { useEffect, useRef, useState } from "react";
import { api, extractError } from "@/lib/apiClient";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import {
  X, Star, MessageSquare, Users, BarChart3, Loader2, Radio, Sparkles, Download, Tag,
} from "lucide-react";

const TAG_COLORS = {
  yellow: { bg: "#fff8ec", border: "#e0b74c", label: "Insight" },
  green:  { bg: "#f0f9f4", border: "#3ea56e", label: "Breakthrough" },
  pink:   { bg: "#fdf1f4", border: "#d76b8f", label: "Concern" },
  blue:   { bg: "#eef3f9", border: "#5b7fb2", label: "Follow-up" },
};

// Order the filter chips explicitly so "Breakthrough" (green) shows first —
// that's usually what facilitators want to jump to when writing recap notes.
const FILTER_ORDER = ["green", "yellow", "pink", "blue"];

function filterChat(chat, active) {
  if (!active || active.size === 0) return chat;
  const wantUntagged = active.has("untagged");
  return chat.filter((m) => {
    if (wantUntagged && !m.facilitator_tag) return true;
    return m.facilitator_tag && active.has(m.facilitator_tag);
  });
}

function ChatFilterBar({ chat, active, onToggle, onClear }) {
  const counts = { untagged: 0, yellow: 0, green: 0, pink: 0, blue: 0 };
  for (const m of chat) {
    if (m.facilitator_tag && counts[m.facilitator_tag] !== undefined) {
      counts[m.facilitator_tag] += 1;
    } else if (!m.facilitator_tag) {
      counts.untagged += 1;
    }
  }
  const total = chat.length;
  const visible = filterChat(chat, active).length;
  const isFiltering = active.size > 0;
  const chip = (key, label, count, palette) => {
    const on = active.has(key);
    return (
      <button
        type="button"
        key={key}
        data-testid={`fc-filter-${key}`}
        onClick={() => onToggle(key)}
        disabled={count === 0}
        className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full border transition-all ${
          count === 0 ? "opacity-40 cursor-not-allowed" : "hover:brightness-105"
        }`}
        style={{
          backgroundColor: on ? palette.border : palette.bg,
          borderColor: palette.border,
          color: on ? "#fff" : palette.border,
        }}
      >
        <span
          className="h-2.5 w-2.5 rounded-full inline-block"
          style={{ backgroundColor: on ? "#fff" : palette.border }}
        />
        {label}
        <span
          className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold ${
            on ? "bg-white/25 text-white" : "bg-white/70"
          }`}
        >
          {count}
        </span>
      </button>
    );
  };
  return (
    <div className="mb-3" data-testid="fc-chat-filter">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="uppercase-label mr-1 inline-flex items-center gap-1">
          <Tag size={11} /> Filter
        </span>
        {FILTER_ORDER.map((k) => chip(k, TAG_COLORS[k].label, counts[k], TAG_COLORS[k]))}
        {chip(
          "untagged",
          "Untagged",
          counts.untagged,
          { bg: "#f4f5f7", border: "#8a94a6" }
        )}
        {isFiltering && (
          <button
            type="button"
            data-testid="fc-filter-clear"
            onClick={onClear}
            className="text-[11px] font-semibold text-[color:var(--mano-primary)] underline ml-1"
          >
            Clear
          </button>
        )}
      </div>
      {isFiltering && (
        <div className="mt-2 text-[11px] text-[color:var(--mano-ink-soft)]" data-testid="fc-filter-count">
          Showing <b>{visible}</b> of {total} reflections
        </div>
      )}
    </div>
  );
}

/** Facilitator's post-session console: chat log + star histogram + notes. */
export default function FacilitatorConsoleModal({ tid, onClose }) {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("overview");
  const [exporting, setExporting] = useState(false);
  const [tagFilter, setTagFilter] = useState(new Set()); // empty = show all
  const chatRef = useRef(null);
  const { user } = useAuth();

  const toggleTagFilter = (key) => {
    setTagFilter((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };
  const clearTagFilter = () => setTagFilter(new Set());

  const refresh = () => {
    api.get(`/admin/datp/therapies/${tid}/facilitator-report`)
      .then((r) => setData(r.data))
      .catch(() => {});
  };
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [tid]);

  const tagMessage = async (msgId, color, note = null) => {
    try {
      await api.post(`/admin/datp/chat/${msgId}/tag`, { color, note });
      // optimistic — refresh
      refresh();
    } catch (e) { toast.error(extractError(e)); }
  };

  const exportPdf = async () => {
    if (!data) return;
    setExporting(true);
    try {
      const [{ default: html2canvas }, { default: jsPDF }] = await Promise.all([
        import("html2canvas"), import("jspdf"),
      ]);
      // Switch to chat tab and let it render before capture
      setTab("chat");
      await new Promise((r) => setTimeout(r, 300));
      const node = chatRef.current;
      if (!node) throw new Error("chat pane not ready");
      const canvas = await html2canvas(node, { backgroundColor: "#ffffff", scale: 2, useCORS: true, logging: false });
      const img = canvas.toDataURL("image/png");
      const pdf = new jsPDF({ orientation: "p", unit: "pt", format: "a4" });
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();
      const margin = 36;
      // Header
      pdf.setFillColor(15, 90, 168);
      pdf.rect(0, 0, pageW, 70, "F");
      pdf.setTextColor(255, 255, 255);
      pdf.setFont("helvetica", "bold"); pdf.setFontSize(16);
      pdf.text(`Manodayam · ${data.therapy.title}`, margin, 32);
      pdf.setFont("helvetica", "normal"); pdf.setFontSize(10);
      const when = data.therapy.scheduled_at ? new Date(data.therapy.scheduled_at).toLocaleString() : "";
      pdf.text(`${when} · ${data.chat_count} reflections · ${data.ratings_summary.count} ratings · avg ${data.ratings_summary.avg ?? "—"}★`, margin, 52);

      // Body — paginate the raster
      const imgW = pageW - margin * 2;
      const imgH = (canvas.height * imgW) / canvas.width;
      let y = 90;
      if (imgH <= pageH - y - margin) {
        pdf.addImage(img, "PNG", margin, y, imgW, imgH);
      } else {
        const pxPerPt = canvas.width / imgW;
        const pageBodyH = pageH - y - margin;
        const slicePxH = Math.floor(pageBodyH * pxPerPt);
        let sy = 0, first = true;
        while (sy < canvas.height) {
          const sh = Math.min(slicePxH, canvas.height - sy);
          const s = document.createElement("canvas");
          s.width = canvas.width; s.height = sh;
          s.getContext("2d").drawImage(canvas, 0, sy, canvas.width, sh, 0, 0, canvas.width, sh);
          if (!first) { pdf.addPage(); y = margin; }
          pdf.addImage(s.toDataURL("image/png"), "PNG", margin, y, imgW, (sh * imgW) / canvas.width);
          sy += sh; first = false;
        }
      }
      // Footer
      pdf.setTextColor(120, 120, 120); pdf.setFontSize(8);
      pdf.text(`Exported ${new Date().toLocaleString()} by ${user?.name || "Facilitator"} — Manodayam DATP`, margin, pageH - 16);

      const safe = (data.therapy.title || "session").replace(/\s+/g, "_").toLowerCase().slice(0, 40);
      pdf.save(`manodayam_${safe}_${new Date().toISOString().slice(0, 10)}.pdf`);
      toast.success("PDF exported");
    } catch (e) {
      console.error(e); toast.error("PDF export failed");
    } finally { setExporting(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-end sm:items-center justify-center p-4" onClick={onClose} data-testid="facilitator-console">
      <div className="bg-white rounded-3xl w-full max-w-3xl max-h-[92vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="p-5 border-b border-[color:var(--mano-border)] flex items-start justify-between gap-3">
          <div className="min-w-0">
            <span className="uppercase-label inline-flex items-center gap-1">
              <Sparkles size={13} /> Facilitator console
            </span>
            <h2 className="font-display text-xl mt-1 truncate">
              {data?.therapy?.title || "Live-group report"}
            </h2>
            {data?.therapy?.scheduled_at && (
              <div className="text-xs text-[color:var(--mano-ink-soft)] mt-0.5 inline-flex items-center gap-1">
                <Radio size={11} /> {new Date(data.therapy.scheduled_at).toLocaleString()}
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              data-testid="fc-export-pdf"
              onClick={exportPdf}
              disabled={exporting || !data}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-[color:var(--mano-primary)] hover:bg-[color:var(--mano-primary-hover)] text-white text-xs font-semibold transition-colors disabled:opacity-60"
            >
              {exporting ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />}
              {exporting ? "Preparing…" : "Export PDF"}
            </button>
            <button onClick={onClose} data-testid="console-close" className="p-1"><X size={18} /></button>
          </div>
        </div>

        {!data && (
          <div className="p-8 flex items-center gap-2 text-[color:var(--mano-ink-soft)]">
            <Loader2 size={16} className="animate-spin" /> Loading…
          </div>
        )}

        {data && (
          <>
            <div className="p-5 grid grid-cols-2 sm:grid-cols-4 gap-2">
              <Tile icon={<Users size={14} />} label="Participants" value={data.participants} />
              <Tile icon={<MessageSquare size={14} />} label="Reflections" value={data.chat_count} />
              <Tile icon={<Star size={14} />} label="Ratings" value={data.ratings_summary.count} />
              <Tile icon={<BarChart3 size={14} />} label="Avg star" value={data.ratings_summary.avg ?? "—"} />
            </div>

            <div className="px-5 pb-3">
              <div className="inline-flex rounded-full border border-[color:var(--mano-border)] bg-white p-1 text-sm">
                {[
                  ["overview", "Overview"],
                  ["chat", `Reflections (${data.chat_count})`],
                  ["ratings", `Ratings (${data.ratings_summary.count})`],
                ].map(([v, l]) => (
                  <button
                    key={v}
                    data-testid={`fc-tab-${v}`}
                    onClick={() => setTab(v)}
                    className={`px-3.5 py-1 rounded-full font-semibold transition-colors ${
                      tab === v ? "bg-[color:var(--mano-primary)] text-white" : "text-[color:var(--mano-ink-soft)]"
                    }`}
                  >
                    {l}
                  </button>
                ))}
              </div>
            </div>

            <div className="px-5 pb-5 overflow-y-auto flex-1">
              {tab === "overview" && <OverviewTab data={data} />}
              {tab === "chat" && (
                <div>
                  <ChatFilterBar
                    chat={data.chat}
                    active={tagFilter}
                    onToggle={toggleTagFilter}
                    onClear={clearTagFilter}
                  />
                  <div ref={chatRef}>
                    <ChatTab
                      chat={filterChat(data.chat, tagFilter)}
                      onTag={tagMessage}
                    />
                  </div>
                </div>
              )}
              {tab === "ratings" && <RatingsTab ratings={data.ratings} />}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function Tile({ icon, label, value }) {
  return (
    <div className="rounded-xl bg-[color:var(--mano-bg)] border border-[color:var(--mano-border)] p-3">
      <div className="uppercase-label inline-flex items-center gap-1">{icon} {label}</div>
      <div className="font-display text-2xl mt-1">{value}</div>
    </div>
  );
}

function OverviewTab({ data }) {
  const total = data.ratings_summary.count || 1;
  return (
    <div className="space-y-4">
      <div className="rounded-2xl border border-[color:var(--mano-border)] p-4">
        <div className="uppercase-label mb-3">Star distribution</div>
        <div className="space-y-1.5">
          {[5, 4, 3, 2, 1].map((s) => {
            const count = data.ratings_summary.histogram[String(s)] || 0;
            const pct = data.ratings_summary.count ? (count / total) * 100 : 0;
            return (
              <div key={s} className="flex items-center gap-2 text-sm" data-testid={`histogram-${s}`}>
                <span className="w-8 inline-flex items-center gap-0.5">{s}<Star size={11} className="fill-amber-400 text-amber-400" /></span>
                <div className="flex-1 h-2 rounded-full bg-[color:var(--mano-bg)] overflow-hidden">
                  <div className="h-full bg-[color:var(--mano-primary)]" style={{ width: `${pct}%` }} />
                </div>
                <span className="w-8 text-right text-xs text-[color:var(--mano-ink-soft)]">{count}</span>
              </div>
            );
          })}
        </div>
      </div>

      {data.chat.length > 0 && (
        <div>
          <div className="uppercase-label mb-2">Most recent reflections</div>
          <ChatTab chat={data.chat.slice(-5)} compact readOnly />
        </div>
      )}
    </div>
  );
}

function ChatTab({ chat, compact = false, onTag = null, readOnly = false }) {
  if (!chat.length) {
    return <div className="text-center text-sm text-[color:var(--mano-ink-soft)] py-6">No chat messages posted.</div>;
  }
  return (
    <div className={compact ? "space-y-1.5" : "space-y-2"} data-testid="fc-chat-list">
      {chat.map((m) => {
        const c = TAG_COLORS[m.facilitator_tag];
        return (
          <div
            key={m.id}
            data-testid={`fc-msg-${m.id}`}
            className="rounded-xl border p-3 transition-colors"
            style={{
              backgroundColor: c?.bg || "#ffffff",
              borderColor: c?.border || "var(--mano-border)",
            }}
          >
            <div className="text-[11px] uppercase-label flex items-center gap-2 flex-wrap">
              <span className="text-[color:var(--mano-primary)] font-bold">{m.user_name}</span>
              <span className="text-[color:var(--mano-ink-soft)]">{new Date(m.at).toLocaleString()}</span>
              {c && (
                <span className="inline-flex items-center gap-1 text-[9px] px-1.5 py-0.5 rounded-full font-semibold" style={{ background: c.border, color: "#fff" }}>
                  <Tag size={9} /> {c.label}
                </span>
              )}
            </div>
            <div className="mt-1 text-sm text-[color:var(--mano-ink)] leading-snug">{m.text}</div>
            {m.facilitator_note && (
              <div className="mt-1.5 text-xs italic text-[color:var(--mano-ink-soft)]">
                Note: {m.facilitator_note}
              </div>
            )}
            {!readOnly && onTag && (
              <div className="mt-2 flex items-center gap-1.5 flex-wrap">
                {Object.entries(TAG_COLORS).map(([key, val]) => (
                  <button
                    key={key}
                    data-testid={`tag-${key}-${m.id}`}
                    onClick={() => onTag(m.id, key)}
                    title={val.label}
                    className={`h-4 w-4 rounded-full border-2 transition-all ${m.facilitator_tag === key ? "ring-2 ring-offset-1 ring-[color:var(--mano-primary)] scale-110" : ""}`}
                    style={{ backgroundColor: val.bg, borderColor: val.border }}
                  />
                ))}
                {m.facilitator_tag && (
                  <button
                    data-testid={`tag-clear-${m.id}`}
                    onClick={() => onTag(m.id, "none")}
                    className="text-[10px] text-[color:var(--mano-ink-soft)] underline ml-1"
                  >
                    clear
                  </button>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function RatingsTab({ ratings }) {  if (!ratings.length) {
    return <div className="text-center text-sm text-[color:var(--mano-ink-soft)] py-6">No ratings collected.</div>;
  }
  return (
    <div className="space-y-2" data-testid="fc-ratings-list">
      {ratings.map((r) => (
        <div key={r.user_id + r.rated_at} className="rounded-xl bg-white border border-[color:var(--mano-border)] p-3">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="text-sm font-semibold">{r.user_name}</div>
            <div className="inline-flex items-center gap-0.5">
              {[1, 2, 3, 4, 5].map((s) => (
                <Star key={s} size={13} className={s <= r.stars ? "fill-amber-400 text-amber-400" : "text-slate-300"} />
              ))}
            </div>
          </div>
          {r.note && <div className="mt-1.5 text-sm italic text-[color:var(--mano-ink)]">"{r.note}"</div>}
          <div className="mt-1 text-[10px] uppercase tracking-wider text-[color:var(--mano-ink-soft)]">
            {new Date(r.rated_at).toLocaleString()}
          </div>
        </div>
      ))}
    </div>
  );
}
